/**
 * Context Menu Handler for Canvas Elements
 * Right-click on elements to open code editor at that location
 */

class ContextMenuHandler {
  editor;
  contextMenu;
  targetComponent;

  constructor(editor) {
    this.editor = editor;
    this.contextMenu = this.createContextMenu();
    this.setupCanvasContextMenu();
  }

  createContextMenu() {
    const menu = document.createElement('div');
    menu.className = 'grapesjs-context-menu';
    menu.style.display = 'none';
    menu.innerHTML = `
      <div class="context-menu-item" data-action="edit-code">
        <i class="fa fa-code"></i>
        <span>Edit Code at This Location</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="copy-component">
        <i class="fa fa-copy"></i>
        <span>Copy Component</span>
      </div>
      <div class="context-menu-item" data-action="duplicate">
        <i class="fa fa-clone"></i>
        <span>Duplicate Component</span>
      </div>
      <div class="context-menu-item" data-action="delete">
        <i class="fa fa-trash"></i>
        <span>Delete Component</span>
      </div>
      <div class="context-menu-separator"></div>
      <div class="context-menu-item" data-action="inspect">
        <i class="fa fa-info-circle"></i>
        <span>Inspect Component</span>
      </div>
    `;

    document.body.appendChild(menu);

    // Set up click handlers
    menu.querySelectorAll('[data-action]').forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const action = item.getAttribute('data-action');
        this.handleAction(action);
        this.hideContextMenu();
      });
    });

    // Hide menu when clicking outside
    document.addEventListener('click', () => {
      this.hideContextMenu();
    });

    return menu;
  }

  setupCanvasContextMenu() {
    // Wait for editor to be fully loaded
    this.editor.on('load', () => {
      const canvasDoc = this.getCanvasDocument();
      if (!canvasDoc) return;

      // Add context menu to canvas
      canvasDoc.addEventListener('contextmenu', (e) => {
        // Find the component at this position
        const component = this.getComponentFromEvent(e);

        if (component) {
          e.preventDefault();
          e.stopPropagation();

          this.targetComponent = component;

          // Show context menu at cursor position
          this.showContextMenu(e.clientX, e.clientY);
        }
      });
    });
  }

  getCanvasDocument() {
    try {
      const iframe = this.editor.Canvas.getFrameEl();
      return iframe ? iframe.contentDocument || iframe.contentWindow.document : null;
    } catch (e) {
      console.error('Failed to get canvas document:', e);
      return null;
    }
  }

  getComponentFromEvent(e) {
    try {
      // Get the element that was clicked
      const element = e.target;

      // Find the corresponding GrapesJS component
      const wrapper = this.editor.DomComponents.getWrapper();
      const component = this.findComponentByElement(wrapper, element);

      return component;
    } catch (e) {
      console.error('Failed to get component from event:', e);
      return null;
    }
  }

  findComponentByElement(parent, element) {
    // Check if this component's element matches
    if (parent.getEl && parent.getEl() === element) {
      return parent;
    }

    // Search children recursively
    const components = parent.components ? parent.components() : [];
    for (let i = 0; i < components.length; i++) {
      const found = this.findComponentByElement(components.at(i), element);
      if (found) return found;
    }

    return null;
  }

  showContextMenu(x, y) {
    this.contextMenu.style.display = 'block';
    this.contextMenu.style.left = `${x}px`;
    this.contextMenu.style.top = `${y}px`;

    // Ensure menu doesn't go off screen
    setTimeout(() => {
      const rect = this.contextMenu.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      if (rect.right > windowWidth) {
        this.contextMenu.style.left = `${windowWidth - rect.width - 10}px`;
      }

      if (rect.bottom > windowHeight) {
        this.contextMenu.style.top = `${windowHeight - rect.height - 10}px`;
      }
    }, 0);
  }

  hideContextMenu() {
    this.contextMenu.style.display = 'none';
  }

  handleAction(action) {
    if (!this.targetComponent) return;

    switch (action) {
      case 'edit-code':
        this.openCodeEditorAtComponent();
        break;

      case 'copy-component':
        this.editor.runCommand('core:copy', {
          component: this.targetComponent,
        });
        this.showNotification('Component copied');
        break;

      case 'duplicate':
        this.duplicateComponent();
        break;

      case 'delete':
        this.targetComponent.remove();
        this.showNotification('Component deleted');
        break;

      case 'inspect':
        this.inspectComponent();
        break;
    }

    this.targetComponent = null;
  }

  openCodeEditorAtComponent() {
    // Trigger the code editor command with the target component
    this.editor.runCommand('preset-mautic:code-edit', {
      targetComponent: this.targetComponent,
    });
  }

  duplicateComponent() {
    try {
      const parent = this.targetComponent.parent();
      if (!parent) return;

      const clone = this.targetComponent.clone();
      const index = parent.components().indexOf(this.targetComponent);
      parent.components().add(clone, { at: index + 1 });

      this.showNotification('Component duplicated');
    } catch (e) {
      console.error('Failed to duplicate component:', e);
      this.showNotification('Failed to duplicate component', 'error');
    }
  }

  inspectComponent() {
    this.editor.select(this.targetComponent);
    this.showNotification('Component selected', 'info');
  }

  showNotification(message, type = 'success') {
    // Create a simple notification
    const notification = document.createElement('div');
    notification.className = `code-editor-notification notification-${type}`;
    notification.style.position = 'fixed';
    notification.style.top = '20px';
    notification.style.right = '20px';
    notification.style.zIndex = '10003';
    notification.textContent = message;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('show');
    }, 10);

    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 2000);
  }

  destroy() {
    if (this.contextMenu && this.contextMenu.parentNode) {
      this.contextMenu.remove();
    }
  }
}

export default ContextMenuHandler;
